import {
  isString,
  isArray,
  isObject,
  isBoolean,
  isNumber,
  isNil,
  isUndefined,
} from 'lodash'
import type { Result } from './result'
import { wrapWithResult, toResult } from './result'
import { joinProperties } from './joinProperties'
import { UnsupportedPropertyError, InvalidValueError } from './errors'

type Validator = (value: any) => Result
type ValidationSchema = {
  [property: string]: Validator
}

export const string = wrapWithResult('string', isString)
export const boolean = wrapWithResult('boolean', isBoolean)
export const number = wrapWithResult('number', isNumber)
export const object = wrapWithResult('object', isObject)
export const array = wrapWithResult('array', isArray)
export const oneOf = (list: any[]) =>
  wrapWithResult(`one of ${list.map(item => `"${item}"`)}`, value =>
    list.includes(value),
  )

export const optional = (validate: Validator) => (value: any) =>
  isNil(value) ? toResult(true) : validate(value)

export const list = (validate: Validator) => (value: any[]) => {
  if (!isArray(value)) {
    return toResult(false, 'array')
  }

  const failedValidationResults = value
    .map(el => {
      const result = validate(el)
      return (
        result.valid === false &&
        toResult(false, `array<${result.expectedType}>`)
      )
    })
    .filter(Boolean)

  return failedValidationResults.length > 0
    ? (failedValidationResults[0] as Result)
    : toResult(true)
}

export const oneOfShape =
  (expectedShape: string, ...shapes: Validator[]) =>
  (value: any) =>
    shapes.some(shape => shape(value).valid)
      ? toResult(true)
      : toResult(false, expectedShape)

export const objectProperties = (schema: ValidationSchema) => (value: any) => {
  const valueTypeValidation = object(value)
  if (!valueTypeValidation.valid) {
    return valueTypeValidation
  }

  const expectedKeys = Object.keys(schema)
  const actualKeys = Object.keys(value)

  const unsupportedProperty = actualKeys.find(
    property => !expectedKeys.includes(property),
  )
  if (unsupportedProperty) {
    return toResult(false, undefined, unsupportedProperty)
  }

  const failedValidationResults = expectedKeys
    .map(property => {
      const validator = schema[property]
      const propertyValue = value[property]
      const result = validator(propertyValue)

      return (
        result.valid === false &&
        toResult(
          false,
          result.expectedType,
          joinProperties(property, result.nestedProperty),
          result.nestedProperty ? result.nestedValue : propertyValue,
        )
      )
    })
    .filter(Boolean)

  return failedValidationResults.length > 0
    ? (failedValidationResults[0] as Result)
    : toResult(true)
}

export const validate =
  (propertyName: string, validator: Validator) => (value: any) => {
    const { valid, expectedType, nestedProperty, nestedValue } =
      validator(value)

    if (!valid) {
      const propertyPath = joinProperties(propertyName, nestedProperty)

      const error = isUndefined(expectedType)
        ? UnsupportedPropertyError(propertyPath)
        : InvalidValueError(
            propertyPath,
            expectedType,
            nestedProperty ? nestedValue : value,
          )

      throw error
    }
  }
